import axios from "axios";
import brushes from "./Brushes";
import { angle360 } from "./utils";
import quantize from "quantize";
import _ from "lodash";

export default class Editor {
    focusedPaths = [];
    selectedPaths = [];

    currentActionBatch = [];
    undoStack = [];
    redoStack = [];

    overlaySvg = null;
    currentLine = null;
    originalColors = {};
    colorPallettes = {};
    uniqueColors = [];
    removeBackgroundOnInit = false;

    constructor({
        target,
        image,
        svgUrl,
        svgData,
        onColorChanged,
        onLoginNeeded,
        onPremiumNeeded,
        onLoading,
        onError,
        doneInit,
        removeBackgroundOnInit = false,
        checkRemoveBackgroundInProgress,
        setRemoveBackgroundInProgress,
    }) {
        // console.log("Editor constructor");
        this.target = target;
        this.image = image;
        this.brushSize = 1;
        this.brushColor = "#7928CA";
        this.mode = "fill";
        this.onColorChanged = onColorChanged;
        this.onLoginNeeded = onLoginNeeded;
        this.onPremiumNeeded = onPremiumNeeded;
        this.onLoading = onLoading;
        this.onError = onError;
        this.isLoading = false;
        this.removeBackgroundOnInit = removeBackgroundOnInit;
        this.checkRemoveBackgroundInProgress = checkRemoveBackgroundInProgress;
        this.setRemoveBackgroundInProgress = setRemoveBackgroundInProgress;

        if (svgData) {
            this.target.innerHTML = svgData;
            this.svgEl = this.target.getElementsByTagName("svg")[0];
            this.init().then(() => {
                if (this.removeBackgroundOnInit) {
                    this.removeBackground();
                }
                doneInit();
            });
        } else {
            axios
                .get(svgUrl)
                .then((response) => {
                    this.target.innerHTML = response.data;

                    this.svgEl = this.target.getElementsByTagName("svg")[0];
                    return this.init().then(() => {
                        if (this.removeBackgroundOnInit) {
                            this.removeBackground();
                        }
                        doneInit();
                    });
                })
                .catch((err) => {
                    axios
                        .get(
                            `https://my.vectorart.ai/storage/v1/object/public/vectorart-upload/${
                                svgUrl.split("/").slice(-2)[0]
                            }`
                        )
                        .then((response) => {
                            this.target.innerHTML = response.data;

                            this.svgEl =
                                this.target.getElementsByTagName("svg")[0];
                            return this.init().then(() => {
                                if (this.removeBackgroundOnInit) {
                                    this.removeBackground();
                                }
                                doneInit();
                            });
                        });
                });
        }
    }

    async init() {
        this.paper = (await import("paper")).default;
        this.paper.setup(this.svgEl);
        this.paper.project.importSVG(this.svgEl);

        this.target.style.position = "relative";

        this.overlaySvg = document.createElement("div");
        this.overlaySvg.style.position = "absolute";
        this.overlaySvg.style.top = 0;
        this.overlaySvg.style.left = 0;
        this.overlaySvg.style.pointerEvents = "none";
        this.overlaySvg.style.width = "100%";
        this.overlaySvg.style.height = "100%";
        this.target.appendChild(this.overlaySvg);
        this.overlaySvg.innerHTML = `<svg height="100%" width="100%">
        <line x1="0" y1="0" x2="200" y2="200" style="display:none;fill:black;stroke:#ffffff;stroke-width:2;stroke-linecap: round;" />
      </svg>`;

        const paths = this.svgEl?.getElementsByTagName("path");
        let i = 0;

        this.paper.project
            .getItems({
                class: this.paper.Path,
            })
            .forEach((path, i) => {
                path.name = `path-${i}`;
            });

        this.svgEl.onmouseleave = this.svgEl.ontouchcancel = (evt) => {
            if (this.currentLine) {
                this.currentLine.style.display = "none";
            }
            this.focusedPaths = [];
            this.currentActionBatch = [];
        };
        this.svgEl.onmouseup = this.svgEl.ontouchend = (evt) => {
            // console.log("mouseup", this.currentActionBatch.length);
            if (this.currentLine) {
                this.currentLine.style.display = "none";
            }
            if (this.currentActionBatch.length) {
                if (this.mode === "gradient") {
                    this.currentActionBatch[0].endPos = {
                        x: evt.clientX,
                        y: evt.clientY,
                    };
                    this.currentActionBatch[0].endOffset = {
                        x: evt.offsetX,
                        y: evt.offsetY,
                    };
                    this.performAction(
                        null,
                        "gradient",
                        null,
                        this.currentActionBatch[0]
                    );
                }
                this.undoStack.push(this.currentActionBatch);
                this.redoStack = [];
                this.currentActionBatch = [];
            }
            this.focusedPaths = [];
        };
        for (const p of paths) {
            p.dataset.index = i;
            this.registerPathEvents(p, paths);
            i++;
        }
    }

    registerPathEvents(p, paths) {
        p.onmousedown = p.ontouchstart = (evt) => {
            this.currentActionBatch = [];
            const list = this.pathsForPoint(evt);
            if (this.mode === "gradient") {
                this.currentActionBatch.push({
                    action: "gradient",
                    startPos: { x: evt.clientX, y: evt.clientY },
                    startOffset: { x: evt.offsetX, y: evt.offsetY },
                    index: list[0].dataset.index,
                    from: p.dataset.index,
                    to: p.dataset.index,
                    selectedPaths: list.map((path) => path.dataset.index),
                });
                return;
            }
            for (const path of list) {
                const action = this.performAction(
                    path,
                    this.mode,
                    this.brushColor
                );
                // console.log("action", action);
                if (action) {
                    this.currentActionBatch.push(action);
                }
            }
        };
        p.onmousemove = p.ontouchmove = (evt) => {
            if (this.mode !== "gradient") {
                for (const index of this.focusedPaths) {
                    paths[index].style.opacity = 1.0;
                    paths[index].style.stroke = null;
                    paths[index].style.strokeWidth = null;
                }
                this.focusedPaths = [];
            }
            const list = this.pathsForPoint(evt);

            if (evt.buttons || evt.touches?.length) {
                if (this.mode === "gradient") {
                    this.drawLine(evt);
                    if (this.currentActionBatch.length === 1) {
                        this.currentActionBatch[0].selectedPaths = _.uniq([
                            ...this.currentActionBatch[0].selectedPaths,
                            ...list.map((path) => path.dataset.index),
                        ]);
                        this.currentActionBatch[0].to = list[0].dataset.index;
                    }
                    return;
                } else if (this.mode === "selection") {
                    this.selectedPaths = _.uniq([
                        ...this.selectedPaths,
                        ...list.map((path) => path.dataset.index),
                    ]);
                }
                for (const path of list) {
                    const action = this.performAction(
                        path,
                        this.mode,
                        this.brushColor
                    );
                    // console.log("action", action);
                    if (action) {
                        this.currentActionBatch.push(action);
                    }
                }
            } else if (this.mode === "fill" || this.mode === "erase") {
                for (const index of this.focusedPaths) {
                    const target = paths[index];
                    target.style.stroke = null;
                    target.style.strokeWidth = null;
                }
                this.focusedPaths = [];
                for (const target of list) {
                    target.style.strokeWidth = "2px";
                    target.style.stroke = "#0D99FF";
                    this.focusedPaths.push(target.dataset.index);
                }
            }
        };
        p.onmouseleave = p.ontouchcancel = (evt) => {
            evt.target.style.opacity = 1.0;
            for (const index of this.focusedPaths) {
                const target = paths[index];
                target.style.stroke = null;
                target.style.strokeWidth = null;
            }
        };
        p.onclick = (evt) => {
            this.currentActionBatch = [];
            this.selectedPaths = [];
            const path = evt.target;
            const pathIndex = path.dataset.index;
            // console.log("click!", evt, i, pathIndex);
            if (this.mode === "gradient") {
                return;
            }
            const action = this.performAction(path, this.mode, this.brushColor);
            if (action) {
                this.undoStack.push([action]);
                this.redoStack = [];
            }
            if (this.mode === "selection") {
                if (
                    this.selectedPaths.length &&
                    !this.selectedPaths.includes(pathIndex)
                ) {
                    this.selectedPaths = [];
                    this.updateSelection();
                } else if (!this.selectedPaths.length) {
                    this.selectedPaths.push(pathIndex);
                    this.updateSelection();
                }
            }
        };
    }

    updateSelection() {
        const paths = this.svgEl?.getElementsByTagName("path");
        for (const selectedPath of this.selectedPaths) {
            const pathCopy = paths[selectedPath].cloneNode(true);
            pathCopy.classList.add("selected-path");
            this.overlaySvg
                .getElementsByTagName("svg")[0]
                .appendChild(pathCopy);
        }
    }

    normalizeColor(color) {
        if (color?.startsWith("#")) {
            return color;
        }
        if (color?.startsWith("rgb")) {
            const [r, g, b] = color
                .substring(4, color.length - 1)
                .split(",")
                .map((x) => parseInt(x));
            let sr = r.toString(16);
            if (sr.length === 1) {
                sr = "0" + sr;
            }
            let sg = g.toString(16);
            if (sg.length === 1) {
                sg = "0" + sg;
            }
            let sb = b.toString(16);
            if (sb.length === 1) {
                sb = "0" + sb;
            }
            return `#${sr}${sg}${sb}`;
        }
        return null;
    }

    drawLine(evt) {
        if (!this.currentActionBatch[0]) {
            return;
        }
        if (!this.currentLine) {
            this.currentLine = this.overlaySvg.getElementsByTagName("line")[0];
        }
        this.currentLine.style.display = "block";
        this.currentLine.setAttribute(
            "x1",
            this.currentActionBatch[0].startOffset.x
        );
        this.currentLine.setAttribute(
            "y1",
            this.currentActionBatch[0].startOffset.y
        );
        this.currentLine.setAttribute("x2", evt.offsetX);
        this.currentLine.setAttribute("y2", evt.offsetY);
    }

    hexToRgb(hex) {
        var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
            ? [
                  parseInt(result[1], 16),
                  parseInt(result[2], 16),
                  parseInt(result[3], 16),
              ]
            : null;
    }

    componentToHex(c) {
        var hex = c.toString(16);
        return hex.length == 1 ? "0" + hex : hex;
    }

    rgbToHex(r, g, b) {
        return (
            "#" +
            this.componentToHex(r) +
            this.componentToHex(g) +
            this.componentToHex(b)
        );
    }

    pathColor(path) {
        return this.normalizeColor(
            path.style.fill ||
                path.style.stroke ||
                path.getAttribute("fill") ||
                path.getAttribute("stroke")
        );
    }

    pathColors(path) {
        const strokeColor = path.getAttribute("stroke");
        const gradientMatch = strokeColor?.match(/url\(#grad-(\d+)\)/);
        if (gradientMatch) {
            const gradientIndex = gradientMatch[1];
            const gradient = this.svgEl?.querySelector(
                `#grad-${gradientIndex}`
            );
            const stops = gradient.querySelectorAll("stop");
            return Array.from(stops).map((stop) =>
                stop.getAttribute("stop-color")
            );
        }
        return [this.pathColor(path)];
    }

    performAction(path, mode, color, action) {
        let result = {};
        if (mode === "gradient") {
            return this.applyGradient(action);
        } else if (mode === "ungradient") {
            return this.removeGradient(action);
        } else if (mode === "unungradient") {
            return this.reapplyGradient(action);
        } else if (mode === "fill") {
            const normalizedColor = this.normalizeColor(color);
            result = {
                index: path.dataset.index,
                action: "fill",
                from: this.normalizeColor(path.style.fill),
                to: normalizedColor,
            };
            path.style.visibility = "visible";
            path.style.fill = normalizedColor;
            path.style.stroke = normalizedColor;
        } else if (mode === "erase") {
            result = {
                index: path.dataset.index,
                action: "erase",
                from: path.style.visibility,
                to: "hidden",
            };
            path.style.visibility = "hidden";
        } else if (mode === "draw") {
            result = {
                index: path.dataset.index,
                action: "draw",
                from: path.style.visibility,
                to: "visible",
            };
            path.style.visibility = "visible";
        } else if (mode === "eyedropper") {
            const color = this.pathColor(path);
            // console.log(color, path.style.fill);
            this.onColorChanged(color);
        }
        if (result.from?.toLowerCase() === result.to?.toLowerCase()) {
            return null;
        }
        return result;
    }

    pathsForPoint(evt) {
        const x = evt.clientX || evt.touches?.[0].clientX;
        const y = evt.clientY || evt.touches?.[0].clientY;
        const deltas = brushes[this.brushSize];
        const result = [];
        for (const delta of deltas) {
            const element = document.elementFromPoint(
                x + this.brushSize * delta[0],
                y + this.brushSize * delta[1]
            );
            if (element && element.tagName === "path") {
                result.push(element);
            }
        }
        return result;
    }

    applyGradient(action) {
        const paths = this.svgEl?.getElementsByTagName("path");
        for (const pathIndex of action.selectedPaths) {
            paths[pathIndex].style.opacity = 1.0;
        }
        if (this.currentActionBatch.length !== 1) {
            return;
        }
        if (!action.to || action.from === action.to) {
            return;
        }
        const uniqSelectedPaths = _.uniq(action.selectedPaths).map(
            (pathIndex) => parseInt(pathIndex)
        );
        const selectedPaperPaths = uniqSelectedPaths.map(
            (pathIndex) =>
                this.paper.project.activeLayer.children[0].children[
                    `path-${pathIndex}`
                ]
        );
        const svgPaths = uniqSelectedPaths.map((pathIndex) => paths[pathIndex]);

        let unitedPath = new this.paper.Path({ insert: false });
        for (const path of selectedPaperPaths) {
            unitedPath = unitedPath.unite(path, { insert: false });
        }

        const newPath = unitedPath.exportSVG();

        this.svgEl.appendChild(newPath);
        this.paper.project.activeLayer.children[0].addChild(unitedPath);

        for (const pathIndex of uniqSelectedPaths) {
            paths[pathIndex].style.display = "none";
        }

        const newPaths = this.svgEl?.getElementsByTagName("path");
        newPath.dataset.index = newPaths.length - 1;
        action.newIndex = newPath.dataset.index;
        unitedPath.name = `path-${action.newIndex}`;
        this.registerPathEvents(newPath, newPaths);

        const gradientChild = document.createElement("defs");

        newPath.setAttribute("fill", `url(#grad-${newPath.dataset.index})`);
        newPath.setAttribute("stroke", `url(#grad-${newPath.dataset.index})`);
        newPath.setAttribute("stroke-width", "0.25");
        this.svgEl.insertBefore(gradientChild, this.svgEl.firstChild);

        const angle = angle360(
            action.startPos.x,
            action.startPos.y,
            action.endPos.x,
            action.endPos.y
        );
        // console.log(angle);

        // for (const pp of svgPaths) {
        //     console.log(pp.dataset.index, pp.getAttribute("fill"));
        // }

        //TODO intersect paths
        //TODO toolbar labels
        //TODO better hover effect

        const colors = [];
        for (const path of svgPaths) {
            if (!path) {
                continue;
            }
            const pathColors = this.pathColors(path);
            // console.log(pathColors);
            colors.push(...pathColors);
        }

        gradientChild.outerHTML = `<defs>
                <linearGradient id="grad-${
                    newPath.dataset.index
                }" gradientTransform="rotate(${angle.toFixed(0)} 0.5 0.5)">
                ${colors.map((color, i) => {
                    return `
                        <stop
                            stop-color="${color}"
                            offset="${i / colors.length}"
                        />
                    `;
                })}
                </linearGradient>
          </defs>`;
    }

    removeGradient(action) {
        const paths = this.svgEl?.getElementsByTagName("path");
        for (const pathIndex of action.selectedPaths) {
            if (paths[pathIndex]) {
                paths[pathIndex].style.display = "block";
            }
        }
        if (paths[action.newIndex]) {
            paths[action.newIndex].style.display = "none";
        }
    }

    reapplyGradient(action) {
        const paths = this.svgEl?.getElementsByTagName("path");
        if (paths[action.newIndex]) {
            paths[action.newIndex].style.display = "block";
        }
        for (const pathIndex of action.selectedPaths) {
            if (paths[pathIndex]) {
                paths[pathIndex].style.display = "none";
            }
        }
    }

    setBrushSize(size) {
        console.log("setBrushSize", size);
        this.brushSize = size;
    }

    setBrushColor(color) {
        console.log("setBrushColor", color);
        this.brushColor = color;
    }

    setMode(mode) {
        console.log("setMode", mode);
        this.mode = mode;
    }

    undo() {
        console.log("undo");
        if (!this.undoStack.length) {
            console.log("nothing to undo");
            return;
        }
        const paths = this.svgEl?.getElementsByTagName("path");
        const actions = this.undoStack.pop();
        for (const action of actions) {
            // console.log("undo", action);
            this.performAction(
                paths[action.index],
                action.action === "erase"
                    ? "draw"
                    : action.action === "gradient"
                    ? "ungradient"
                    : action.action,
                action.from,
                action
            );
        }
        this.redoStack.push(actions);
    }

    redo() {
        console.log("redo");
        if (!this.redoStack.length) {
            console.log("nothing to redo");
            return;
        }
        const paths = this.svgEl?.getElementsByTagName("path");
        const actions = this.redoStack.pop();
        for (const action of actions) {
            // console.log("redo", action);
            this.performAction(
                paths[action.index],
                action.action === "gradient" ? "unungradient" : action.action,
                action.to,
                action
            );
        }
        this.undoStack.push(actions);
    }

    download() {
        console.log("download");
        const svg = this.svgEl?.cloneNode(true);
        const paths = svg.getElementsByTagName("path");
        for (const path of paths) {
            if (path.style.visibility === "hidden") {
                path.remove();
            }
        }
        const svgData = svg.outerHTML;
        const svgBlob = new Blob([svgData], {
            type: "image/svg+xml;charset=utf-8",
        });
        const svgUrl = URL.createObjectURL(svgBlob);
        const downloadLink = document.createElement("a");
        downloadLink.href = svgUrl;
        downloadLink.download = "image.svg";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    }

    removeBackground() {
        console.log("removeBackground", this.isLoading);

        // Check if there's already a remove background operation in progress
        if (this.checkRemoveBackgroundInProgress) {
            const existingPromise = this.checkRemoveBackgroundInProgress();
            if (existingPromise) {
                console.log("removeBackground already in progress, reusing promise");
                return existingPromise;
            }
        }

        if (this.isLoading) {
            return;
        }
        this.isLoading = true;
        this.onLoading(true);

        let promise;

        if (!this.image) {
            const svg = this.svgEl?.cloneNode(true);
            const paths = svg.getElementsByTagName("path");
            for (const path of paths) {
                if (path.style.visibility === "hidden") {
                    path.remove();
                }
            }
            const svgData = svg.outerHTML;
            promise = axios
                .post(`/api/image/generic/remove-background`, svgData, {
                    headers: {
                        "Content-Type": "image/svg+xml",
                    },
                })
                .then((response) => {
                    const actions = [];
                    const indexes = response.data.indexes;
                    const paths = this.svgEl?.getElementsByTagName("path");
                    for (const index of indexes) {
                        const path = paths[index];
                        path.style.visibility = "hidden";
                        actions.push({
                            index: path.dataset.index,
                            action: "erase",
                            from: path.style.visibility,
                            to: "hidden",
                        });
                    }
                    this.undoStack.push(actions);
                })
                .catch((err) => {
                  if (err?.response?.status === 400) {
                    this.onLoginNeeded?.();
                  } else if (err?.response?.status === 401) {
                    this.onPremiumNeeded?.();
                  } else {
                    this.onError?.(err?.response?.data?.error || err?.message || "Failed to remove background. Please try again.");
                  }
                })
                .finally(() => {
                    this.isLoading = false;
                    this.onLoading(false);
                    if (this.setRemoveBackgroundInProgress) {
                        this.setRemoveBackgroundInProgress(false, null);
                    }
                });
        } else {
            promise = axios
                .post(`/api/image/${this.image}/remove-background`)
                .then((response) => {
                    const actions = [];
                    const indexes = response.data.indexes;
                    const paths = this.svgEl?.getElementsByTagName("path");
                    for (const index of indexes) {
                        const path = paths[index];
                        path.style.visibility = "hidden";
                        actions.push({
                            index: path.dataset.index,
                            action: "erase",
                            from: path.style.visibility,
                            to: "hidden",
                        });
                    }
                    this.undoStack.push(actions);
                })
                .catch((err) => {
                  if (err?.response?.status === 400) {
                    this.onLoginNeeded?.();
                  } else if (err?.response?.status === 401) {
                    this.onPremiumNeeded?.();
                  } else {
                    this.onError?.(err?.response?.data?.error || err?.message || "Failed to remove background. Please try again.");
                  }
                })
                .finally(() => {
                    this.isLoading = false;
                    this.onLoading(false);
                    if (this.setRemoveBackgroundInProgress) {
                        this.setRemoveBackgroundInProgress(false, null);
                    }
                });
        }

        // Store the promise in shared state to prevent duplicate calls
        if (this.setRemoveBackgroundInProgress) {
            this.setRemoveBackgroundInProgress(true, promise);
        }

        return promise;
    }

    quantizeColors(factor) {
        const paths = this.svgEl?.getElementsByTagName("path");
        if (factor >= 1 && this.originalColors) {
            console.log("show original");
            for (const path of paths) {
                const pathColor = this.originalColors[path.dataset.index];
                if (!pathColor) {
                    continue;
                }
                path.style.fill = pathColor;
                path.style.stroke = pathColor;
            }
            return;
        }

        const colors = (this.uniqueColors || []).map((color) =>
            this.hexToRgb(color)
        );
        if (!colors.length) {
            for (const path of paths) {
                const pathColor = this.pathColor(path);
                if (!pathColor) {
                    continue;
                }
                colors.push(this.hexToRgb(pathColor));
                this.originalColors[path.dataset.index] = pathColor;
            }
            this.uniqueColors = _.uniq(Object.values(this.originalColors));
        }

        const numColors = Math.max(
            2,
            parseInt(factor * Math.min(32, this.uniqueColors.length))
        );
        console.log("Reduce it ", colors.length, factor, numColors);
        const palette =
            this.colorPallettes[factor] || quantize(colors, numColors);
        this.colorPallettes[factor] = palette;

        const actions = [];

        for (const path of paths) {
            const pathColor = this.originalColors[path.dataset.index];
            if (!pathColor) {
                continue;
            }
            const newColor = this.rgbToHex(
                ...palette.nearest(this.hexToRgb(pathColor))
            );

            actions.push({
                index: path.dataset.index,
                action: "fill",
                from: this.normalizeColor(path.style.fill),
                to: newColor,
            });
            path.style.fill = newColor;
            path.style.stroke = newColor;
        }

        this.undoStack.push(actions);
    }
}
